import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  MdDarkMode,
  MdLightMode,
  MdPlayCircleOutline,
  MdEdit,
  MdPanTool,
  MdAutorenew,
  MdPlayArrow,
  MdPause,
  MdRefresh,
  MdNavigateNext,
  MdMenu,
  MdClose,
} from "react-icons/md";
import "./App.css";

// --- Parser Logic ---

const parseTimeTag = (tag) => {
  // tag format: [10], [10.5], [1:30], [1:30.5]
  const content = tag.replace(/[\[\]]/g, "").trim();
  if (content.includes(":")) {
    const [mm, ss] = content.split(":").map(Number);
    return mm * 60 + ss;
  }
  return Number(content);
};

const parseText = (text) => {
  // 1. Split by time tags
  // Regex to find [digits], [digits.digits], [digits:digits], or [digits:digits.digits]
  // Allow optional spaces
  const timeTagRegex =
    /(\[\s*(?:\d+(?:\.\d+)?:\d+(?:\.\d+)?|\d+(?:\.\d+)?)\s*\])/g;
  const parts = text.split(timeTagRegex);

  const segments = [];
  let currentTime = 0;

  // Regex for checking if a part is a time tag (no global flag)
  const isTimeTag = /^\[\s*(?:\d+(?:\.\d+)?:\d+(?:\.\d+)?|\d+(?:\.\d+)?)\s*\]$/;

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (isTimeTag.test(part)) {
      currentTime = parseTimeTag(part);
    } else if (part.trim() !== "") {
      segments.push({
        startTime: currentTime,
        content: part, // Raw content, will be parsed for markdown later
        id: i,
      });
    }
  }

  // If no time tags at start, ensure first segment has time 0
  if (segments.length > 0 && segments[0].startTime !== 0) {
    // If the first segment doesn't start at 0, we might want to treat it as 0 or keep it as is.
    // For this app, let's assume implicit 0 start if not specified.
  }

  return segments;
};

const MarkdownRenderer = ({ text }) => {
  // Simple parser for:
  // **bold**
  // *italic*
  // __underline__
  // {color:red text}
  // {mark:yellow text}
  // # Heading

  // We will process line by line for headings
  const lines = text.split("\n");

  return (
    <>
      {lines.map((line, lineIndex) => {
        let content = line;
        let Type = "p";

        if (content.startsWith("# ")) {
          Type = "h2";
          content = content.substring(2);
        }

        // Process inline styles
        // Combined regex for all inline tokens
        // 1. Color: \{color:([a-z]+) (.*?)\}
        // 2. Mark: \{mark:([a-z]+) (.*?)\}
        // 3. Bold: \*\*(.*?)\*\*
        // 4. Italic: \*(.*?)\*
        // 5. Underline: __(.*?)__

        const inlineRegex =
          /(\{color:[a-z]+ .*?\}|\{mark:[a-z]+ .*?\}|\*\*.*?\*\*|\*.*?\*|__.*?__)/g;
        const parts = content.split(inlineRegex);

        const renderedParts = parts.map((part, partIndex) => {
          if (part.startsWith("{color:")) {
            const match = part.match(/\{color:([a-z]+) (.*?)\}/);
            if (match)
              return (
                <span key={partIndex} style={{ color: match[1] }}>
                  {match[2]}
                </span>
              );
          }
          if (part.startsWith("{mark:")) {
            const match = part.match(/\{mark:([a-z]+) (.*?)\}/);
            if (match)
              return (
                <span key={partIndex} style={{ backgroundColor: match[1] }}>
                  {match[2]}
                </span>
              );
          }
          if (part.startsWith("**") && part.endsWith("**")) {
            return <strong key={partIndex}>{part.slice(2, -2)}</strong>;
          }
          if (part.startsWith("*") && part.endsWith("*")) {
            return <em key={partIndex}>{part.slice(1, -1)}</em>;
          }
          if (part.startsWith("__") && part.endsWith("__")) {
            return <u key={partIndex}>{part.slice(2, -2)}</u>;
          }
          return part;
        });

        return <Type key={lineIndex}>{renderedParts}</Type>;
      })}
    </>
  );
};

const DraggableButton = ({ onClick, children }) => {
  // Initial position: Bottom-Left (approx 20px from left, 80px from bottom)
  const [position, setPosition] = useState({
    x: 20,
    y: window.innerHeight - 80,
  });
  const isDragging = useRef(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const offset = useRef({ x: 0, y: 0 });
  const lastClickTime = useRef(0);

  // Mouse Handlers
  const handleMouseDown = (e) => {
    isDragging.current = true;
    dragStartPos.current = { x: e.clientX, y: e.clientY };
    offset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    setPosition({
      x: e.clientX - offset.current.x,
      y: e.clientY - offset.current.y,
    });
  };

  const handleMouseUp = (e) => {
    isDragging.current = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);

    // Check if it was a click or drag (threshold 5px)
    const dist = Math.sqrt(
      Math.pow(e.clientX - dragStartPos.current.x, 2) +
        Math.pow(e.clientY - dragStartPos.current.y, 2)
    );

    if (dist < 5) {
      const now = Date.now();
      if (now - lastClickTime.current > 300) {
        onClick();
        lastClickTime.current = now;
      }
    }
  };

  // Touch Handlers
  const handleTouchStart = (e) => {
    isDragging.current = true;
    const touch = e.touches[0];
    dragStartPos.current = { x: touch.clientX, y: touch.clientY };
    offset.current = {
      x: touch.clientX - position.x,
      y: touch.clientY - position.y,
    };
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    const touch = e.touches[0];
    setPosition({
      x: touch.clientX - offset.current.x,
      y: touch.clientY - offset.current.y,
    });
  };

  const handleTouchEnd = (e) => {
    isDragging.current = false;
    const touch = e.changedTouches[0];
    const dist = Math.sqrt(
      Math.pow(touch.clientX - dragStartPos.current.x, 2) +
        Math.pow(touch.clientY - dragStartPos.current.y, 2)
    );

    if (dist < 5) {
      const now = Date.now();
      if (now - lastClickTime.current > 300) {
        onClick();
        lastClickTime.current = now;
      }
    }
  };

  return (
    <button
      className="floating-next-btn"
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ left: position.x, top: position.y }}
    >
      {children}
    </button>
  );
};

// --- Helper ---
const formatTime = (seconds) => {
  if (seconds < 60) {
    return seconds.toFixed(1) + "秒";
  }
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  const sStr = s < 10 ? `0${s.toFixed(1)}` : s.toFixed(1);
  return `${m}分 ${sStr}秒`;
};

// --- Components ---

function App() {
  const [text, setText] = useState(`# プレゼン原稿サンプル

[0] みなさん、こんにちは。
[3] 本日は、**新しいプロジェクト**についてお話しします。

[7] {mark:yellow 重要なポイント}は3つあります。
[10] 1つ目は、{color:red スピード}です。
[13] 2つ目は、__品質__です。
[16] そして3つ目は、*コスト*です。

[20] ご清聴ありがとうございました。`);

  const [mode, setMode] = useState("edit"); // 'edit' | 'play'
  const [scrollMode, setScrollMode] = useState("manual"); // 'auto' | 'manual'
  const [manualIndex, setManualIndex] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [theme, setTheme] = useState("light"); // 'dark' | 'light'
  const [isPlaying, setIsPlaying] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const elapsedTimeRef = useRef(0); // Ref to keep track of time for animation loop
  const startTimeRef = useRef(null);
  const requestRef = useRef(null);
  const activeSegmentRef = useRef(null);

  const segments = useMemo(() => parseText(text), [text]);

  // Timer Logic
  const animate = React.useCallback((time) => {
    if (!startTimeRef.current) {
      startTimeRef.current = time - elapsedTimeRef.current * 1000;
    }
    const now = (time - startTimeRef.current) / 1000;
    setElapsedTime(now);
    elapsedTimeRef.current = now;
    requestRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    if (isPlaying) {
      requestRef.current = requestAnimationFrame(animate);
    } else {
      cancelAnimationFrame(requestRef.current);
      startTimeRef.current = null;
    }
    return () => cancelAnimationFrame(requestRef.current);
  }, [isPlaying, animate]);

  const handleStart = () => setIsPlaying(true);
  const handleStop = () => setIsPlaying(false);
  const handleReset = () => {
    setIsPlaying(false);
    setElapsedTime(0);
    elapsedTimeRef.current = 0;
    startTimeRef.current = null;
    setManualIndex(0);
  };

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  // Find active segment based on time
  const timeBasedIndex = segments.findIndex((seg, idx) => {
    const nextSeg = segments[idx + 1];
    return (
      elapsedTime >= seg.startTime &&
      (!nextSeg || elapsedTime < nextSeg.startTime)
    );
  });

  // Determine which segment is "active" for display purposes
  const displayIndex = scrollMode === "manual" ? manualIndex : timeBasedIndex;

  // Auto Scroll Logic
  useEffect(() => {
    if (mode === "play" && activeSegmentRef.current) {
      // In manual mode, we scroll when manualIndex changes (handled by ref update)
      // In auto mode, we scroll when timeBasedIndex changes
      activeSegmentRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [displayIndex, mode]); // Depend on displayIndex instead of elapsedTime

  const handleNext = () => {
    if (manualIndex < segments.length - 1) {
      setManualIndex((prev) => prev + 1);
    }
  };

  const getSegmentStatus = (seg, idx) => {
    if (scrollMode !== "manual" || idx !== manualIndex) return null;

    const nextSeg = segments[idx + 1];
    const endTime = nextSeg ? nextSeg.startTime : Infinity;

    if (elapsedTime < seg.startTime) return "early";
    if (elapsedTime >= seg.startTime && elapsedTime < endTime) return "ontime";
    return "late";
  };

  const getTimeDisplay = (seg, idx) => {
    if (scrollMode !== "manual" || idx !== manualIndex) return null;

    const nextSeg = segments[idx + 1];
    const endTime = nextSeg ? nextSeg.startTime : Infinity;

    if (elapsedTime < seg.startTime) {
      return `あと ${formatTime(seg.startTime - elapsedTime)}`;
    }
    if (
      elapsedTime >= seg.startTime &&
      elapsedTime < endTime &&
      endTime !== Infinity
    ) {
      return `残り ${formatTime(endTime - elapsedTime)}`;
    }
    if (elapsedTime >= endTime) {
      return `超過 ${formatTime(elapsedTime - endTime)}`;
    }
    return null;
  };

  const getProgress = (seg, idx) => {
    if (scrollMode !== "manual" || idx !== manualIndex) return 0;

    const nextSeg = segments[idx + 1];
    const endTime = nextSeg ? nextSeg.startTime : Infinity;
    const prevSeg = segments[idx - 1];
    const prevEndTime = prevSeg ? prevSeg.startTime : 0;

    if (elapsedTime < seg.startTime) {
      // Early: Progress from prevEndTime to seg.startTime
      const totalWait = seg.startTime - prevEndTime;
      const waited = elapsedTime - prevEndTime;
      return totalWait > 0 ? (waited / totalWait) * 100 : 0;
    }
    if (
      elapsedTime >= seg.startTime &&
      elapsedTime < endTime &&
      endTime !== Infinity
    ) {
      // On Time: Progress from seg.startTime to endTime
      const duration = endTime - seg.startTime;
      const elapsed = elapsedTime - seg.startTime;
      return duration > 0 ? (elapsed / duration) * 100 : 0;
    }
    if (elapsedTime >= endTime) {
      return 100;
    }
    return 0;
  };

  return (
    <div className="App" data-theme={theme}>
      <header className="App-header-bar">
        <h1>PA NEXT</h1>
        <div className="controls">
          {/* Primary Action: Start/Stop/Pause */}
          {mode === "play" &&
            (!isPlaying ? (
              <button onClick={handleStart} className="primary-btn">
                <MdPlayArrow /> スタート/再開
              </button>
            ) : (
              <button onClick={handleStop} className="primary-btn">
                <MdPause /> 一時停止
              </button>
            ))}

          {/* Menu Toggle */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="menu-btn"
          >
            {isMenuOpen ? <MdClose /> : <MdMenu />}
          </button>

          {/* Dropdown Menu */}
          {isMenuOpen && (
            <div className="menu-dropdown">
              <div className="menu-item">
                <button onClick={toggleTheme}>
                  {theme === "dark" ? (
                    <>
                      <MdLightMode /> ライトモード
                    </>
                  ) : (
                    <>
                      <MdDarkMode /> ダークモード
                    </>
                  )}
                </button>
              </div>

              <div className="menu-item">
                {mode === "edit" ? (
                  <button
                    onClick={() => {
                      setMode("play");
                      setIsMenuOpen(false);
                    }}
                  >
                    <MdPlayCircleOutline /> 再生画面へ
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setMode("edit");
                      setIsMenuOpen(false);
                    }}
                  >
                    <MdEdit /> 編集画面に戻る
                  </button>
                )}
              </div>

              {mode === "play" && (
                <div className="menu-item">
                  <button
                    onClick={() => {
                      setScrollMode((prev) =>
                        prev === "auto" ? "manual" : "auto"
                      );
                      setIsMenuOpen(false);
                    }}
                  >
                    {scrollMode === "auto" ? (
                      <>
                        <MdPanTool /> 手動モードへ
                      </>
                    ) : (
                      <>
                        <MdAutorenew /> 自動モードへ
                      </>
                    )}
                  </button>
                </div>
              )}

              <div className="menu-item">
                <button
                  onClick={() => {
                    handleReset();
                    setIsMenuOpen(false);
                  }}
                >
                  <MdRefresh /> リセット
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="timer">{formatTime(elapsedTime)}</div>
      </header>

      <main className="App-main">
        {mode === "edit" ? (
          <textarea
            className="editor"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="【凡例】
# 見出し
**太字**
*斜体*
__下線__
{color:red 赤色テキスト}
{mark:yellow ハイライトテキスト}
タイムタグは[秒数]、[分:秒]、小数点付きも可。[0]は省略可能です。"
          />
        ) : (
          <div className="teleprompter">
            <div className="spacer"></div>
            {segments.map((seg, idx) => {
              const isActive = idx === displayIndex;
              const status = getSegmentStatus(seg, idx);
              const timeDisplay = getTimeDisplay(seg, idx);
              const progress = getProgress(seg, idx);

              return (
                <div
                  key={seg.id}
                  ref={isActive ? activeSegmentRef : null}
                  className={`segment ${isActive ? "active" : ""} ${
                    status ? `status-${status}` : ""
                  }`}
                  onClick={() => {
                    if (scrollMode === "manual") {
                      setManualIndex(idx);
                    } else {
                      // Optional: Click to jump time in auto mode
                      setElapsedTime(seg.startTime);
                      elapsedTimeRef.current = seg.startTime;
                      startTimeRef.current = null;
                    }
                  }}
                >
                  <div className="segment-time">
                    [{formatTime(seg.startTime)}]
                  </div>
                  {timeDisplay && (
                    <>
                      <div className="time-indicator">{timeDisplay}</div>
                      <div className="progress-bar-container">
                        <div
                          className="progress-bar-fill"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                    </>
                  )}
                  <div className="segment-content">
                    <MarkdownRenderer text={seg.content} />
                  </div>
                </div>
              );
            })}
            <div className="spacer"></div>
            {mode === "play" && scrollMode === "manual" && (
              <DraggableButton onClick={handleNext}>
                <MdNavigateNext
                  style={{ fontSize: "3rem", transform: "rotate(90deg)" }}
                />
              </DraggableButton>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
